import { call, cancelled, put, select } from 'redux-saga/effects'
import { t } from 'i18next'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import { clearAuth } from '@/containers/Login/AuthSlice'
import { showSnackbar, SNACKBAR_TYPES } from './notifications/notification'
import { isJsonResponse, nonJsonResponseError } from './nonJsonResponseError'
import { ipNotAllowedActions } from './ipNotAllowed'
import * as Sentry from '@sentry/react'
import { markReportedToSentry, REPORTED_BY_CALL_API } from './sentryBeforeSend'
import { isAddressLinkedToContract } from './isAddressLinkedToContract'

const HTTP_CONFLICT = 409

export function* callApi({
  endpoint,
  method = 'GET',
  payload = null,
  queryParams = {},
  enqueue = null,
  showErrorSnackbar = true,
  asteriskReplacement = null,
}) {
  const controller = new AbortController()
  const { authTokenSession, country } = yield select(selectAuthData)
  try {
    const url = buildUrl(endpoint, country, queryParams, asteriskReplacement)
    const config = yield call(getAPIConfig, {
      method,
      authTokenSession,
      body: method !== 'GET' ? payload : null,
    })

    const response = yield call(fetch, url, {
      ...config,
      signal: controller.signal,
    })

    const contentType = response.headers.get('content-type')
    if (!isJsonResponse(contentType)) {
      throw nonJsonResponseError(response, contentType)
    }

    const data = yield call([response, response.json])

    if (!response.ok) {
      const error = new Error(apiErrorMessage(data, response.status))
      error.status = response.status
      error.responseData = data
      error.validationErrors = data?.errors || {}
      throw error
    }
    return data
  } catch (error) {
    let errorMessage = error.message
    const ipRefusalActions = ipNotAllowedActions(error.status, error.responseData)
    if (ipRefusalActions !== null) {
      for (const action of ipRefusalActions) {
        yield put(action)
      }
      markReportedToSentry(error)
      throw error
    }
    if (error.status === 401) {
      yield put(clearAuth())
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message: readApiErrorMessage(error.responseData) ?? "Erreur d'autorisation. Veuillez vous reconnecter.",
        })
      }
      throw error
    }
    if (error.status === 422) {
      errorMessage = t('common:api_validation_error')
    }
    if (showErrorSnackbar && enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: errorMessage,
      })
    }
    if (isHandledBusinessError(error)) {
      markReportedToSentry(error)
      Sentry.addBreadcrumb({
        category: 'api.business_error',
        message: String(error.responseData?.error?.code ?? error.responseData?.code ?? error.status),
        level: 'info',
        data: { endpoint, status: error.status },
      })
      throw error
    }
    if (import.meta.env.PROD && error.status !== 422) {
      markReportedToSentry(error)
      Sentry.captureException(new Error('API call failed'), {
        tags: {
          ...REPORTED_BY_CALL_API,
          'api.endpoint': endpoint,
          'api.method': method,
          'api.status': error.status,
          'api.error_code': error.responseData?.error?.code,
        },
        extra: { error },
      })
    }
    throw error
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

const isHandledBusinessError = error =>
  error.status === HTTP_CONFLICT || isAddressLinkedToContract(error.status, error.responseData)

const readApiErrorMessage = data => {
  if (typeof data === 'string' && data !== '') return data
  if (typeof data?.error === 'string' && data.error !== '') return data.error
  if (typeof data?.error?.message === 'string' && data.error.message !== '') return data.error.message
  if (typeof data?.message === 'string' && data.message !== '') return data.message

  return null
}

export const apiErrorMessage = (data, status) => readApiErrorMessage(data) ?? `Error ${status}`

export const buildUrl = (endpoint, country, queryParams = {}, asteriskReplacement = null) => {
  if (!endpoint) {
    throw new Error('Configuration error. Missing endpoint URL. Please logout and login again')
  }

  let processedEndpoint = endpoint
  if (asteriskReplacement !== null && endpoint.includes('*')) {
    processedEndpoint = endpoint.replace('*', asteriskReplacement)
  }

  const hasParams = processedEndpoint.includes('?')
  const langParam = `lang=${country}`
  const queryString = new URLSearchParams(queryParams).toString()

  let url = processedEndpoint
  url += hasParams ? '&' + langParam : '?' + langParam
  if (queryString) url += '&' + queryString

  return url
}
